import type { JSX } from 'preact';

interface SymProps {
  size?: number;
  sw?: number;
}

type SymComp = (p: SymProps) => JSX.Element;

function S({ size = 24, sw = 1.7, children }: SymProps & { children: JSX.Element | JSX.Element[] }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none"
         stroke="currentColor" stroke-width={sw}
         stroke-linecap="round" stroke-linejoin="round">
      {children}
    </svg>
  );
}

export function SymBook(p: SymProps) {
  return (
    <S {...p}>
      <path d="M5 12c5-2 11-2 19 2 8-4 14-4 19-2v24c-5-2-11-2-19 2-8-4-14-4-19-2V12z"/>
      <path d="M24 14v24"/>
      <path d="M10 18c3-1 7-1 11 1" opacity=".55"/>
      <path d="M10 23c3-1 7-1 11 1" opacity=".55"/>
      <path d="M27 19c3-1 7-1 11 0" opacity=".55"/>
      <path d="M27 24c3-1 7-1 11 0" opacity=".55"/>
    </S>
  );
}

export function SymScroll(p: SymProps) {
  return (
    <S {...p}>
      <rect x="4" y="9" width="9" height="30"/>
      <line x1="8.5" y1="11" x2="8.5" y2="37" opacity=".45"/>
      <line x1="2" y1="9" x2="15" y2="9"/>
      <line x1="2" y1="39" x2="15" y2="39"/>
      <rect x="35" y="9" width="9" height="30"/>
      <line x1="39.5" y1="11" x2="39.5" y2="37" opacity=".45"/>
      <line x1="33" y1="9" x2="46" y2="9"/>
      <line x1="33" y1="39" x2="46" y2="39"/>
      <line x1="13" y1="14" x2="35" y2="14"/>
      <line x1="13" y1="34" x2="35" y2="34"/>
      <path d="M16 20h16M16 24h16M16 28h10" opacity=".7" stroke-width="1.4"/>
    </S>
  );
}

export function SymQuill(p: SymProps) {
  return (
    <S {...p}>
      <path d="M40 6c-4 0-10 2-16 8s-10 14-12 22c8-2 16-6 22-12s8-12 8-18z"/>
      <path d="M40 6L12 36" opacity=".85"/>
      <path d="M34 10l-4 4M30 12l-5 5M27 15l-5 5M24 18l-6 6M22 22l-5 5M20 26l-4 4" opacity=".55" stroke-width="1.3"/>
      <path d="M12 36l-3 6"/>
      <path d="M9 42l3 1"/>
    </S>
  );
}

export function SymMountain(p: SymProps) {
  return (
    <S {...p}>
      <circle cx="32" cy="16" r="6" opacity=".55"/>
      <path d="M4 38L20 16l9 12 4-5 11 15"/>
      <path d="M14 28l3 4M22 22l4 6" opacity=".55"/>
    </S>
  );
}

export function SymPalm(p: SymProps) {
  return (
    <S {...p}>
      <path d="M24 8c-6 4-12 12-14 22"/>
      <path d="M24 8c6 4 12 12 14 22"/>
      <path d="M24 8v36"/>
      <path d="M24 14c-3 0-6 2-8 4M24 14c3 0 6 2 8 4" opacity=".7"/>
      <path d="M24 22c-4 0-8 2-11 5M24 22c4 0 8 2 11 5" opacity=".55"/>
      <path d="M24 30c-4 1-8 4-10 7M24 30c4 1 8 4 10 7" opacity=".4"/>
    </S>
  );
}

export function SymGate(p: SymProps) {
  return (
    <S {...p}>
      <path d="M10 42V22c0-7 6-14 14-14s14 7 14 14v20"/>
      <path d="M16 42V24c0-4 4-8 8-8s8 4 8 8v18"/>
      <path d="M24 16v26" opacity=".5"/>
    </S>
  );
}

export function SymSunrise(p: SymProps) {
  return (
    <S {...p}>
      <path d="M4 36h40"/>
      <path d="M12 36a12 12 0 0 1 24 0"/>
      <path d="M24 14v-6M10 22l-4-4M38 22l4-4M16 18l-2-3M32 18l2-3" opacity=".7"/>
    </S>
  );
}

export function SymTablet(p: SymProps) {
  return (
    <S {...p}>
      <path d="M12 10c0-2 2-3 4-3h6c2 0 4 1 4 3M22 10c0-2 2-3 4-3h6c2 0 4 1 4 3"/>
      <path d="M12 10v28c0 2 2 3 4 3h6c2 0 4-1 4-3M22 10v28c0 2 2 3 4 3h6c2 0 4-1 4-3"/>
      <path d="M16 16h6M16 20h6M16 24h4M26 16h6M26 20h6M26 24h4" opacity=".55"/>
    </S>
  );
}

export function SymTree(p: SymProps) {
  return (
    <S {...p}>
      <path d="M24 44V22"/>
      <path d="M24 22c-8 0-12-6-12-11 0-3 2-5 5-5 2 0 3 1 4 3 1-3 4-5 7-5s6 2 7 5c1-2 2-3 4-3 3 0 5 2 5 5 0 5-4 11-12 11"/>
      <path d="M24 30c-3 0-6-2-6-5M24 30c3 0 6-2 6-5" opacity=".5"/>
    </S>
  );
}

export function SymPath(p: SymProps) {
  return (
    <S {...p}>
      <path d="M4 44 C 14 36 26 22 40 6"/>
      <path d="M16 44 C 22 38 32 26 40 6"/>
      <path d="M6 34l1-3M9 32v-3M12 30l1-3" stroke-width="1.5" opacity=".85"/>
      <ellipse cx="32" cy="38" rx="3.5" ry="2.2" fill="currentColor" stroke="none" opacity=".75"/>
    </S>
  );
}

export function SymFlame(p: SymProps) {
  return (
    <S {...p}>
      <path d="M10 32c0-3 6-5 14-5s14 2 14 5v2c0 3-6 5-14 5s-14-2-14-5v-2z"/>
      <path d="M10 32h28"/>
      <path d="M38 32l4-2" opacity=".7"/>
      <path d="M24 27v-6"/>
      <path d="M24 6c-3 4-5 7-5 11 0 3 2 4 5 4s5-1 5-4c0-4-2-7-5-11z"/>
    </S>
  );
}

export function SymKey(p: SymProps) {
  return (
    <S {...p}>
      <circle cx="24" cy="38" r="6"/>
      <circle cx="24" cy="38" r="2" fill="currentColor" stroke="none" opacity=".6"/>
      <path d="M24 32V8"/>
      <path d="M24 14h6"/>
      <path d="M24 9h5"/>
    </S>
  );
}

export function SymOlive(p: SymProps) {
  return (
    <S {...p}>
      <path d="M42 8 C 34 14 24 24 14 38"/>
      <path d="M40 8 C 34 4 28 4 28 12 C 34 14 40 12 40 8 Z" fill="currentColor" stroke="none" opacity=".78"/>
      <path d="M34 14 C 40 14 42 22 36 22 C 32 22 30 18 34 14 Z" fill="currentColor" stroke="none" opacity=".78"/>
      <path d="M28 20 C 22 18 18 20 20 26 C 26 28 30 24 28 20 Z" fill="currentColor" stroke="none" opacity=".78"/>
      <path d="M22 28 C 28 28 30 34 24 34 C 20 34 18 32 22 28 Z" fill="currentColor" stroke="none" opacity=".78"/>
      <ellipse cx="14" cy="38" rx="2.4" ry="3" fill="currentColor" stroke="none"/>
      <ellipse cx="10" cy="42" rx="2.4" ry="3" fill="currentColor" stroke="none"/>
      <ellipse cx="17" cy="43" rx="2.4" ry="3" fill="currentColor" stroke="none"/>
    </S>
  );
}

export const PLAN_ICON_MAP: Record<string, { Sym: SymComp; pal: string }> = {
  'nt-90':           { Sym: SymScroll,   pal: 'indigo'   },
  'chrono-365':      { Sym: SymBook,     pal: 'forest'   },
  'psalms':          { Sym: SymQuill,    pal: 'garnet'   },
  'gospels-30':      { Sym: SymSunrise,  pal: 'ocean'    },
  'proverbs-daily':  { Sym: SymTree,     pal: 'amber'    },
  'holy-week':       { Sym: SymPalm,     pal: 'rose'     },
  'sermon-mount':    { Sym: SymMountain, pal: 'teal'     },
  'nt-psalms-180':   { Sym: SymQuill,    pal: 'violet'   },
  'wisdom-60':       { Sym: SymOlive,    pal: 'olive'    },
  'ot-365':          { Sym: SymTablet,   pal: 'copper'   },
  'nt-40':           { Sym: SymFlame,    pal: 'violet'   },
  'ot-40':           { Sym: SymTablet,   pal: 'bronze'   },
  'chrono-bible-40': { Sym: SymPath,     pal: 'pine'     },
  'chrono-nt':       { Sym: SymPath,     pal: 'midnight' },
};

export function PlanTile({ planId, size = 44 }: { planId: string; size?: number }) {
  const entry = PLAN_ICON_MAP[planId] ?? { Sym: SymBook as SymComp, pal: 'indigo' };
  const { Sym, pal } = entry;
  return (
    <div class={`plan-tile plan--${pal}`}
         style={{ width: `${size}px`, height: `${size}px`, borderRadius: `${Math.round(size * 0.27)}px` }}>
      <Sym size={Math.round(size * 0.6)} sw={1.7} />
    </div>
  );
}
